import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EnglishIntro from './components/EnglishIntro';
import EnglishQuestion from './components/EnglishQuestion';
import EnglishResult from './components/EnglishResult';
import TestResumeBanner from '@/components/feature/TestResumeBanner';
import { TEST_PROGRESS_KEYS, readProgress, writeProgress, clearProgress } from '@/lib/testProgress';
import { buildEnglishQuestions } from '@/lib/englishQuestions';
import { englishLevelMeta, type EnglishLevel } from '@/mocks/englishWords';

export type EnglishStep = 'intro' | 'test' | 'result';

interface EnglishProgress {
  level: EnglishLevel;
  current: number;
  answers: Record<string, number>;
}

export default function EnglishTestPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<EnglishStep>('intro');
  const [level, setLevel] = useState<EnglishLevel>('elementary');
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [resumeDetail, setResumeDetail] = useState<string | null>(null);

  const questions = useMemo(() => buildEnglishQuestions(level), [level]);

  // 마운트 시 저장된 진행 상황 확인
  useEffect(() => {
    const saved = readProgress<EnglishProgress>(TEST_PROGRESS_KEYS.english);
    if (saved && saved.level && (Object.keys(saved.answers || {}).length > 0 || saved.current > 0)) {
      setLevel(saved.level);
      const done = Math.min(saved.current + 1, buildEnglishQuestions(saved.level).length);
      const label = englishLevelMeta.find((l) => l.id === saved.level)?.label || '영단어';
      setResumeDetail(`${label} 검사 ${done} / ${buildEnglishQuestions(saved.level).length}문항 진행 중`);
    }
  }, []);

  // 검사 진행 상황 자동 저장
  useEffect(() => {
    if (step !== 'test') return;
    if (Object.keys(answers).length === 0 && current === 0) return;
    writeProgress(TEST_PROGRESS_KEYS.english, { level, current, answers });
  }, [step, level, current, answers]);

  const clearAll = () => {
    clearProgress(TEST_PROGRESS_KEYS.english);
    setResumeDetail(null);
  };

  const resume = () => {
    const saved = readProgress<EnglishProgress>(TEST_PROGRESS_KEYS.english);
    if (saved) {
      setLevel(saved.level);
      setCurrent(saved.current || 0);
      setAnswers(saved.answers || {});
      setStep('test');
    }
  };

  const startTest = (selectedLevel: EnglishLevel) => {
    clearAll();
    setLevel(selectedLevel);
    setAnswers({});
    setCurrent(0);
    setStep('test');
  };

  const retake = () => {
    clearAll();
    setAnswers({});
    setCurrent(0);
    setStep('intro');
  };

  // 결과 도달 시 저장된 진행 상황 정리
  useEffect(() => {
    if (step === 'result') clearAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const levelLabel = englishLevelMeta.find((l) => l.id === level)?.label || '영어 단어';

  return (
    <div className="min-h-screen bg-background-50">
      {/* Top Nav */}
      <nav className="bg-background-50 border-b border-background-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="w-8 h-8 flex items-center justify-center text-foreground-600 hover:text-foreground-950 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-accent-500 rounded-lg">
              <i className="ri-book-open-line text-white text-xs"></i>
            </div>
            <span className="font-semibold text-foreground-950 text-sm">영어 단어 검사</span>
          </div>

          {step === 'test' && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full font-medium">
                {levelLabel}
              </span>
            </div>
          )}
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 md:py-10">
        {step === 'intro' && (
          <>
            {resumeDetail && (
              <div className="max-w-2xl mx-auto mb-6">
                <TestResumeBanner detail={resumeDetail} onResume={resume} onReset={clearAll} />
              </div>
            )}
            <EnglishIntro onStart={startTest} />
          </>
        )}
        {step === 'test' && (
          <EnglishQuestion
            questions={questions}
            current={current}
            answers={answers}
            onSelect={(questionId, optionIdx) => {
              setAnswers((prev) => ({ ...prev, [questionId]: optionIdx }));
              if (current < questions.length - 1) {
                setCurrent((c) => c + 1);
              } else {
                setStep('result');
              }
            }}
            onNext={() => setCurrent((c) => c + 1)}
            onPrev={() => setCurrent((c) => Math.max(0, c - 1))}
            onSubmit={() => setStep('result')}
          />
        )}
        {step === 'result' && (
          <EnglishResult
            level={level}
            questions={questions}
            answers={answers}
            onRetake={retake}
          />
        )}
      </main>
    </div>
  );
}